import {
  DEFAULT_LATEST_N,
  trainingCountForPreset,
  usesSessionWindow,
} from "../AddWidgetModal/constants";
import { PRESET_OFFSETS } from "../Processing/datePresets.mjs";

// Shared date-range formatting for PDF widget snapshots.
// Table and radar widgets both surface a "MM/DD/YYYY - MM/DD/YYYY" range in
// their PDF header from the same set of builder-misc date fields.

export interface BuilderDateMisc {
  dateRange?: string;
  showAdvancedDate?: boolean;
  startDateType?: "relative" | "absolute" | "latest_n";
  /** latest_n only: how many dates with data the window holds. */
  startDateLatestN?: number;
  startDateAbsolute?: string;
  startDateOffset?: number;
  endDateType?: "relative" | "absolute";
  endDateAbsolute?: string;
  endDateOffset?: number;
}

const fmtDate = (d: Date) =>
  `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;

/**
 * Resolve the date range a widget is filtered to into a display string.
 * Returns "" when no misc is supplied.
 */
export function formatBuilderDateRange(misc?: BuilderDateMisc): string {
  if (!misc) return "";

  const presetOffset = PRESET_OFFSETS[misc.dateRange ?? ""] ?? -28;

  const resolveDate = (
    type: BuilderDateMisc["startDateType"],
    absolute: string | undefined,
    offset: number | undefined,
    fallbackOffset: number,
  ): string => {
    if (type === "absolute" && absolute) {
      const [y, m, d] = absolute.split("-").map(Number);
      return fmtDate(new Date(y, m - 1, d));
    }
    const now = new Date();
    now.setDate(now.getDate() + (offset ?? fallbackOffset));
    return fmtDate(now);
  };

  // A session window has no calendar start until the backend resolves it against the
  // data, so the header names the window instead of printing a range that is not real.
  // Both routes to one: the Advanced mode, and the training-count presets.
  if (usesSessionWindow(misc)) {
    const n = misc.showAdvancedDate
      ? misc.startDateLatestN ?? DEFAULT_LATEST_N
      : trainingCountForPreset(misc.dateRange) ?? DEFAULT_LATEST_N;
    return `Last ${n} session${n === 1 ? "" : "s"}`;
  }

  if (misc.showAdvancedDate) {
    const start = resolveDate(misc.startDateType, misc.startDateAbsolute, misc.startDateOffset, presetOffset);
    const end = resolveDate(misc.endDateType, misc.endDateAbsolute, misc.endDateOffset, 0);
    return `${start} - ${end}`;
  }

  const now = new Date();
  const startD = new Date(now);
  startD.setDate(startD.getDate() + presetOffset);
  return `${fmtDate(startD)} - ${fmtDate(now)}`;
}
